import { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { motion, AnimatePresence } from 'motion/react'
import { cn } from '@/shared/lib/utils'
import { useTelegram } from '@/shared/lib/telegram'

export interface ProductDescriptionProps {
  /**
   * Текст описания
   */
  description: string
  /**
   * Максимальная длина до сворачивания (в символах)
   * @default 300
   */
  maxLength?: number
}

/**
 * Виджет: Блок описания товара
 * 
 * Отображает описание с возможностью развернуть/свернуть.
 * Автоматически сворачивает длинные тексты.
 * Скрывается, если описание отсутствует или пустое.
 */
export function ProductDescription({
  description,
  maxLength = 300,
}: ProductDescriptionProps) {
  const { hapticFeedback } = useTelegram()
  const [isExpanded, setIsExpanded] = useState(false)
  
  // Проверяем наличие описания
  if (!description || description.trim() === '') {
    return null
  }
  
  const shouldTruncate = description.length > maxLength
  const displayText = shouldTruncate && !isExpanded
    ? description.slice(0, maxLength) + '...'
    : description
  
  const handleToggle = () => {
    hapticFeedback.selectionChanged()
    setIsExpanded(!isExpanded)
  }
  
  return (
    <div className="px-4 py-6 space-y-4">
      <h2 className="text-xl font-semibold text-ink dark:text-white">Описание</h2>
      
      <div className="space-y-4">
        <p className="text-base text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line">
          {displayText}
        </p>
        
        {shouldTruncate && (
          <button
            onClick={handleToggle}
            className="flex items-center gap-2 text-primary dark:text-primary-light font-medium touch-target"
          >
            <span>{isExpanded ? 'Свернуть описание' : 'Развернуть описание'}</span>
            <motion.div
              animate={{ rotate: isExpanded ? 180 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <ChevronDown className="h-4 w-4" />
            </motion.div>
          </button>
        )}
      </div>
    </div>
  )
}


